/**
 * Пазы детали (detailData.grooves) для 3D-модуля.
 * Контур — мм в СК детали (X=length, Y=width); depth — в толщину.
 */
import {createShape} from "../../../customer/processing/DetailProcessing/Canvas/CanvasCalcPositions/createShape";
import {orderContourPoints} from "../../../../utils/processing/orderContourPoints";
import {dedupeMachiningById} from "./machiningDedup";

const FACE_POSITIONS = new Set(["TOP", "BOTTOM"]);
const SIDE_POSITIONS = new Set(["LEFT", "RIGHT", "FRONT", "BACK"]);

export const isRenderableGroove = (groove) =>
    Boolean(
        groove
        && (String(groove.name || "").toLowerCase().includes("groove")
            || groove.contour?.points?.length >= 3)
        && (groove.contour?.points?.length >= 3 || Number(groove.length) > 0),
    );

export const getGrooveFace = (groove) => {
    const pos = groove?.contour?.position;
    if (pos && SIDE_POSITIONS.has(pos)) return pos;
    if (pos && FACE_POSITIONS.has(pos)) return pos;
    if (String(groove?.type || "").toUpperCase() === "SIDE") return pos || "LEFT";
    return "TOP";
};

export const isFaceGroove = (groove) => FACE_POSITIONS.has(getGrooveFace(groove));

export const isSideGroove = (groove) =>
    SIDE_POSITIONS.has(getGrooveFace(groove))
    || String(groove?.type || "").toUpperCase() === "SIDE"
    || (Array.isArray(groove?.sideContours) && groove.sideContours.length > 0
        && !FACE_POSITIONS.has(groove?.contour?.position));

export const isThroughFaceGroove = (groove, panelHeightMm) => {
    if (!isFaceGroove(groove)) return false;
    if (groove?.through === true) return true;
    const depth = Number(groove.depth);
    const height = Number(panelHeightMm);
    if (!Number.isFinite(height) || height <= 0) return false;
    if (!Number.isFinite(depth)) return false;
    return depth >= height - 0.5;
};

export const normalizeGrooveContourPoints = (points = []) =>
    orderContourPoints(points).map((p) => ({
        ...p,
        y: Number(p?.y) || 0,
        z: Number(p?.z) || 0,
        arcParams: p?.arcParams
            ? {...p.arcParams, y: Number(p.arcParams.y) || 0}
            : p?.arcParams,
    }));

export const grooveContourShape = (groove) => {
    const points = groove?.contour?.points;
    if (!points?.length) return null;
    return createShape(normalizeGrooveContourPoints(points));
};

export const classifyDetailGrooves = (detailData) => {
    const height = Number(detailData?.height) || 18;
    const list = dedupeMachiningById((detailData?.grooves || []).filter(isRenderableGroove));
    const throughFace = [];
    const blindFace = [];
    const side = [];

    for (const groove of list) {
        if (isSideGroove(groove) && !isFaceGroove(groove)) {
            side.push(groove);
            continue;
        }
        if (isThroughFaceGroove(groove, height)) {
            throughFace.push(groove);
        } else {
            blindFace.push(groove);
        }
    }

    return {throughFace, blindFace, side, height};
};

/** Extrude глухого паза в пласти: локальные мм, Z = толщина. */
export const getBlindFaceGroovePose = (groove, heightMm) => {
    const height = Math.max(Number(heightMm) || 18, 1);
    const depth = Math.min(
        Math.max(Number(groove.depth) || height * 0.4, 0.1),
        height - 0.05,
    );
    const face = getGrooveFace(groove);
    const z = face === "BOTTOM" ? 0.05 : height - depth - 0.05;
    return {
        key: groove.id ?? `groove-blind-${face}`,
        shape: grooveContourShape(groove),
        depth,
        z,
        face,
    };
};
